import { useState } from 'react';
import { ErrorBanner, Loading, Modal } from '../../components/ui';
import cmitLogo from '../../assets/cmit-logo.png';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import { formatDate } from '../../lib/format';
import type { Employee, SelfReview, SelfReviewForm, SelfReviewKind, SelfReviewRating } from '../../lib/types';

/**
 * The CMIT Employee Self-Assessment Form, on screen.
 *
 * Laid out as the Word document is — one sheet of bordered tables with navy
 * section bands, the three-column Category / Rating / Comments table, tick
 * boxes for the four levels and the signature lines at the foot — because
 * this is the form people already know, and the point is that filling it in
 * here feels like filling in that. The wording comes from the API
 * (api/src/lib/selfReviewForm.ts); for a saved review, the categories come
 * from the review's own snapshot (migration 171).
 *
 * The whole sheet is one draft, saved with one button, like the paper form.
 *
 * The same sheet is the Manager Assessment Form (`kind="manager"`): the
 * document is identical apart from its title (migration 173). A manager
 * review is written on the Employees page and saved privately, or saved and
 * shared — only then does it appear on the person's People record, where it
 * is shown read-only.
 */

interface ItemDraft {
  key: string;
  sectionTitle: string;
  category: string;
  description: string;
  rating: SelfReviewRating | null;
  comments: string;
}

/** A company core value on the sheet: rated 1-5 stars, not on the form's four levels. */
interface ValueDraft {
  valueId: number | null;
  name: string;
  description: string | null;
  stars: number | null;
  comments: string;
}

interface Draft {
  jobTitle: string;
  reviewerEmployeeId: string;
  periodFrom: string;
  periodTo: string;
  goal1: string;
  goal2: string;
  goal3: string;
  overallRating: SelfReviewRating | null;
  summary: string;
  employeeSignature: string;
  employeeSignedDate: string;
  reviewerSignature: string;
  reviewerSignedDate: string;
  items: ItemDraft[];
  values: ValueDraft[];
}

const RATINGS: SelfReviewRating[] = ['exceeds', 'meets', 'needs_improvement', 'unacceptable'];

const day = (v: string | null) => v?.slice(0, 10) ?? '';

function draftFrom(form: SelfReviewForm, review: SelfReview | null, defaults: SelfReviewDefaults): Draft {
  const items: ItemDraft[] = review
    ? review.items.map((i) => ({
        key: i.ItemKey,
        sectionTitle: i.SectionTitle,
        category: i.Category,
        description: i.Description,
        rating: i.Rating,
        comments: i.Comments ?? '',
      }))
    : form.ratedSections.flatMap((s) =>
        s.items.map((i) => ({
          key: i.key,
          sectionTitle: s.title,
          category: i.category,
          description: i.description,
          rating: null,
          comments: '',
        })),
      );
  return {
    jobTitle: review ? review.JobTitle ?? '' : defaults.jobTitle ?? '',
    reviewerEmployeeId: String((review ? review.ReviewerEmployeeId : defaults.reviewerEmployeeId) ?? ''),
    periodFrom: day(review?.PeriodFrom ?? null),
    periodTo: day(review?.PeriodTo ?? null),
    goal1: review?.Goal1 ?? '',
    goal2: review?.Goal2 ?? '',
    goal3: review?.Goal3 ?? '',
    overallRating: review?.OverallRating ?? null,
    summary: review?.Summary ?? '',
    employeeSignature: review?.EmployeeSignature ?? '',
    employeeSignedDate: day(review?.EmployeeSignedDate ?? null),
    reviewerSignature: review?.ReviewerSignature ?? '',
    reviewerSignedDate: day(review?.ReviewerSignedDate ?? null),
    items,
    /* A saved review's own snapshot; a new one is drawn from the V/TO today. */
    values: review
      ? review.values.map((v) => ({
          valueId: v.ValueId,
          name: v.Name,
          description: v.Description,
          stars: v.Stars,
          comments: v.Comments ?? '',
        }))
      : form.coreValues.map((v) => ({ valueId: null, name: v.Name, description: v.Description, stars: null, comments: '' })),
  };
}

interface SelfReviewDefaults {
  jobTitle: string | null;
  reviewerEmployeeId: number | null;
}

export function SelfReviewDialog({
  kind = 'self',
  readOnly = false,
  employeeId,
  employeeName,
  firstName,
  selfReviewId,
  defaults,
  employees,
  onClose,
  onSaved,
}: {
  kind?: SelfReviewKind;
  /** Shown, not edited — a shared manager review on the People record. */
  readOnly?: boolean;
  employeeId: number;
  employeeName: string;
  /** For "Save and share with …". */
  firstName?: string;
  /** Null for a new review. */
  selfReviewId: number | null;
  defaults: SelfReviewDefaults;
  employees: Employee[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const form = useResource<SelfReviewForm>(`/people/self-reviews/form?kind=${kind}`);
  const review = useResource<SelfReview>(selfReviewId === null ? null : `/people/self-reviews/${selfReviewId}`);

  const loading = form.loading || review.loading;
  const error = form.error ?? review.error;

  if (loading || error || !form.data || (selfReviewId !== null && !review.data)) {
    return (
      <Modal title={kind === 'manager' ? 'Review' : 'Self review'} onClose={onClose} size="xl">
        {error ? <ErrorBanner message={error} /> : <Loading label="Loading the form" />}
      </Modal>
    );
  }

  return (
    <SelfReviewSheet
      kind={kind}
      readOnly={readOnly}
      sharedUtc={review.data?.SharedUtc ?? null}
      firstName={firstName ?? employeeName}
      form={form.data}
      initial={draftFrom(form.data, review.data, defaults)}
      employeeId={employeeId}
      employeeName={review.data?.EmployeeName ?? employeeName}
      selfReviewId={selfReviewId}
      employees={employees}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

function SelfReviewSheet({
  kind,
  readOnly,
  sharedUtc,
  firstName,
  form,
  initial,
  employeeId,
  employeeName,
  selfReviewId,
  employees,
  onClose,
  onSaved,
}: {
  kind: SelfReviewKind;
  readOnly: boolean;
  sharedUtc: string | null;
  firstName: string;
  form: SelfReviewForm;
  initial: Draft;
  employeeId: number;
  employeeName: string;
  selfReviewId: number | null;
  employees: Employee[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
  };
  const setItem = (key: string, patch: Partial<ItemDraft>) => {
    setDraft((d) => ({ ...d, items: d.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
    setDirty(true);
  };
  const setValue = (name: string, patch: Partial<ValueDraft>) => {
    setDraft((d) => ({ ...d, values: d.values.map((v) => (v.name === name ? { ...v, ...patch } : v)) }));
    setDirty(true);
  };

  /* Escape and the backdrop close the dialog, and a self-assessment is a lot
     of typing to lose to a stray key. */
  const close = () => {
    if (dirty && !window.confirm('Close without saving? What you have written here will be lost.')) return;
    onClose();
  };

  /* `shared` is sent for a manager review only: a self review is never private. */
  const save = async (shared?: boolean) => {
    setSaving(true);
    setError(null);
    const body = {
      ...(kind === 'manager' && shared !== undefined ? { shared } : {}),
      jobTitle: draft.jobTitle,
      reviewerEmployeeId: draft.reviewerEmployeeId ? Number(draft.reviewerEmployeeId) : null,
      periodFrom: draft.periodFrom,
      periodTo: draft.periodTo,
      goal1: draft.goal1,
      goal2: draft.goal2,
      goal3: draft.goal3,
      overallRating: draft.overallRating,
      summary: draft.summary,
      employeeSignature: draft.employeeSignature,
      employeeSignedDate: draft.employeeSignedDate,
      reviewerSignature: draft.reviewerSignature,
      reviewerSignedDate: draft.reviewerSignedDate,
      items: draft.items.map((i) => ({ key: i.key, rating: i.rating, comments: i.comments })),
      values: draft.values.map((v) => ({ valueId: v.valueId, name: v.name, stars: v.stars, comments: v.comments })),
    };
    try {
      if (selfReviewId === null) await api.post(`/people/self-reviews/employee/${employeeId}?kind=${kind}`, body);
      else await api.put(`/people/self-reviews/${selfReviewId}`, body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the self review');
      setSaving(false);
    }
  };

  /* In the order the items were snapshotted, which is the form's order. */
  const sections: { title: string; items: ItemDraft[] }[] = [];
  for (const item of draft.items) {
    const existing = sections.find((s) => s.title === item.sectionTitle);
    if (existing) existing.items.push(item);
    else sections.push({ title: item.sectionTitle, items: [item] });
  }

  return (
    <Modal
      title={
        kind === 'manager'
          ? selfReviewId === null
            ? 'New review'
            : 'Review'
          : selfReviewId === null
            ? 'New self review'
            : 'Self review'
      }
      onClose={close}
      size="xl"
      footer={
        readOnly ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        ) : kind === 'manager' ? (
          <>
            <span className={`badge ${sharedUtc ? 'badge-good' : 'badge-warning'}`} style={{ marginRight: 'auto' }}>
              {sharedUtc ? `Shared ${formatDate(sharedUtc)}` : 'Private'}
            </span>
            <button type="button" className="btn btn-ghost" onClick={close}>
              Cancel
            </button>
            {sharedUtc ? (
              <>
                <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => void save(false)}>
                  Make private
                </button>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save(true)}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => void save(false)}>
                  {saving ? 'Saving…' : 'Save privately'}
                </button>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save(true)}>
                  Save and share with {firstName}
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={close}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save self review'}
            </button>
          </>
        )
      }
    >
      {error && <ErrorBanner message={error} />}
      {kind === 'manager' && !readOnly && (
        <p className="small muted">
          <strong>Save privately</strong> keeps it here on the Employees page, where only admins see
          it. <strong>Save and share</strong> puts it on {firstName}'s People record.
        </p>
      )}

      {/* A disabled fieldset disables every control in the sheet at once,
          which is the whole of "read-only" for a shared review. */}
      <fieldset className="sr-sheet" disabled={readOnly}>
        <div className="sr-masthead">
          <img src={cmitLogo} alt="CMIT Solutions — Your Technology Team" />
          <h1>{form.title}</h1>
        </div>

        <table className="sr-table">
          <colgroup>
            <col style={{ width: '34%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '44%' }} />
          </colgroup>
          <tbody>
            <tr>
              <th colSpan={3} className="sr-band sr-caps">
                {form.employeeInformation}
              </th>
            </tr>
            <tr>
              <td>
                <span className="sr-label">Employee Name:</span> {employeeName}
              </td>
              <td colSpan={2}>
                <label className="sr-inline">
                  <span className="sr-label">Job Title:</span>
                  <input
                    className="sr-input"
                    value={draft.jobTitle}
                    onChange={(e) => set('jobTitle', e.target.value)}
                  />
                </label>
              </td>
            </tr>
            <tr>
              <td>
                <label className="sr-stacked">
                  <span className="sr-label">Supervisor/Reviewer:</span>
                  <select
                    className="sr-input"
                    value={draft.reviewerEmployeeId}
                    onChange={(e) => set('reviewerEmployeeId', e.target.value)}
                  >
                    <option value="" />
                    {employees.map((emp) => (
                      <option key={emp.EmployeeId} value={emp.EmployeeId}>
                        {emp.FullName}
                      </option>
                    ))}
                  </select>
                </label>
              </td>
              <td colSpan={2}>
                <span className="sr-label">Review Period</span>
                <div className="sr-period">
                  <label className="sr-inline">
                    From:
                    <input
                      className="sr-input"
                      type="date"
                      value={draft.periodFrom}
                      onChange={(e) => set('periodFrom', e.target.value)}
                    />
                  </label>
                  <label className="sr-inline">
                    To:
                    <input
                      className="sr-input"
                      type="date"
                      value={draft.periodTo}
                      onChange={(e) => set('periodTo', e.target.value)}
                    />
                  </label>
                </div>
              </td>
            </tr>

            {sections.map((section) => (
              <RatedSection
                key={section.title}
                title={section.title}
                items={section.items}
                labels={form.ratingLabels}
                onChange={setItem}
              />
            ))}

            {draft.values.length > 0 && <CoreValuesSection values={draft.values} onChange={setValue} />}

            <tr>
              <th colSpan={3} className="sr-band sr-caps">
                {form.goals.title}
              </th>
            </tr>
            <tr>
              <td colSpan={3}>
                <p className="sr-label">{form.goals.prompt}</p>
                {(['goal1', 'goal2', 'goal3'] as const).map((key) => (
                  <textarea
                    key={key}
                    className="sr-input sr-goal"
                    rows={2}
                    placeholder="TBD"
                    value={draft[key]}
                    onChange={(e) => set(key, e.target.value)}
                  />
                ))}
              </td>
            </tr>

            <tr>
              <th colSpan={3} className="sr-band sr-caps">
                {form.overall.title}
              </th>
            </tr>
            <tr>
              <td colSpan={3} className="sr-overall-cell">
                <div className="sr-overall" role="group" aria-label={form.overall.title}>
                  {form.overall.levels.map((level) => (
                    <label key={level.value} className="sr-overall-level">
                      <span>
                        <input
                          type="checkbox"
                          className="sr-box"
                          checked={draft.overallRating === level.value}
                          onChange={() =>
                            set('overallRating', draft.overallRating === level.value ? null : level.value)
                          }
                        />{' '}
                        <strong>{level.label}</strong>
                      </span>
                      <em>{level.description}</em>
                    </label>
                  ))}
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="sr-table sr-gap">
          <tbody>
            <tr>
              <th className="sr-band sr-band-black sr-caps">{form.summaryTitle}</th>
            </tr>
            <tr>
              <td>
                <textarea
                  className="sr-input"
                  rows={5}
                  aria-label="Review summary"
                  value={draft.summary}
                  onChange={(e) => set('summary', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <th className="sr-band sr-band-black sr-caps">{form.acknowledgement.title}</th>
            </tr>
            <tr>
              <td>
                <p>{form.acknowledgement.text}</p>
                <SignatureLine
                  label="Employee Signature:"
                  name={draft.employeeSignature}
                  date={draft.employeeSignedDate}
                  onName={(v) => set('employeeSignature', v)}
                  onDate={(v) => set('employeeSignedDate', v)}
                />
              </td>
            </tr>
            <tr>
              <td>
                <SignatureLine
                  label="Reviewer Signature:"
                  name={draft.reviewerSignature}
                  date={draft.reviewerSignedDate}
                  onName={(v) => set('reviewerSignature', v)}
                  onDate={(v) => set('reviewerSignedDate', v)}
                />
              </td>
            </tr>
          </tbody>
        </table>
        <p className="xs subtle mt-2">
          A signature here is the signer's typed name and the date they signed. It is not an
          electronic signature.
        </p>
      </fieldset>
    </Modal>
  );
}

function RatedSection({
  title,
  items,
  labels,
  onChange,
}: {
  title: string;
  items: ItemDraft[];
  labels: Record<SelfReviewRating, string>;
  onChange: (key: string, patch: Partial<ItemDraft>) => void;
}) {
  return (
    <>
      <tr>
        <th colSpan={3} className="sr-band">
          {title}
        </th>
      </tr>
      <tr className="sr-colhead">
        <th>Performance Category</th>
        <th>Rating</th>
        <th>Comments and Examples</th>
      </tr>
      {items.map((item) => (
        <tr key={item.key}>
          <td>
            <strong>{item.category}:</strong>
            <br />
            <em>{item.description}</em>
          </td>
          <td>
            <div className="sr-ratings" role="group" aria-label={`${item.category} rating`}>
              {RATINGS.map((r) => (
                <label key={r} className="sr-rating">
                  <input
                    type="checkbox"
                    className="sr-box"
                    checked={item.rating === r}
                    onChange={() => onChange(item.key, { rating: item.rating === r ? null : r })}
                  />
                  {labels[r]}
                </label>
              ))}
            </div>
          </td>
          <td>
            <textarea
              className="sr-input sr-comments"
              aria-label={`${item.category} comments and examples`}
              value={item.comments}
              onChange={(e) => onChange(item.key, { comments: e.target.value })}
            />
          </td>
        </tr>
      ))}
    </>
  );
}

/**
 * The company's core values, each rated 1 to 5 stars — the V/TO's values, not
 * the form's section A, which is titled "Core Values" but holds competencies.
 * Clicking the star already given clears it, so a rating can be taken back.
 */
function CoreValuesSection({
  values,
  onChange,
}: {
  values: ValueDraft[];
  onChange: (name: string, patch: Partial<ValueDraft>) => void;
}) {
  const rated = values.filter((v) => v.stars !== null);
  const average = rated.length ? rated.reduce((n, v) => n + (v.stars ?? 0), 0) / rated.length : null;
  return (
    <>
      <tr>
        <th colSpan={3} className="sr-band">
          Company Core Values
          {average !== null && <span className="sr-values-avg"> · average {average.toFixed(1)} of 5</span>}
        </th>
      </tr>
      <tr className="sr-colhead">
        <th>Core Value</th>
        <th>Rating (1–5 stars)</th>
        <th>Comments and Examples</th>
      </tr>
      {values.map((value) => (
        <tr key={value.name}>
          <td>
            <strong>{value.name}</strong>
            {value.description && (
              <>
                <br />
                <em>{value.description}</em>
              </>
            )}
          </td>
          <td>
            <div className="sr-stars" role="radiogroup" aria-label={`${value.name} rating`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={value.stars === n}
                  aria-label={`${n} star${n === 1 ? '' : 's'}`}
                  title={`${n} of 5`}
                  className={`sr-star${value.stars !== null && n <= value.stars ? ' is-on' : ''}`}
                  onClick={() => onChange(value.name, { stars: value.stars === n ? null : n })}
                >
                  ★
                </button>
              ))}
              <span className="sr-stars-num">{value.stars === null ? 'Not rated' : `${value.stars} of 5`}</span>
            </div>
          </td>
          <td>
            <textarea
              className="sr-input sr-comments"
              aria-label={`${value.name} comments and examples`}
              value={value.comments}
              onChange={(e) => onChange(value.name, { comments: e.target.value })}
            />
          </td>
        </tr>
      ))}
    </>
  );
}

function SignatureLine({
  label,
  name,
  date,
  onName,
  onDate,
}: {
  label: string;
  name: string;
  date: string;
  onName: (v: string) => void;
  onDate: (v: string) => void;
}) {
  return (
    <div className="sr-signature">
      <label className="sr-inline grow">
        {label}
        <input className="sr-input sr-sign" value={name} placeholder="Typed full name" onChange={(e) => onName(e.target.value)} />
      </label>
      <label className="sr-inline">
        Date:
        <input className="sr-input" type="date" value={date} onChange={(e) => onDate(e.target.value)} />
      </label>
    </div>
  );
}
